/**
 * Unsaved-changes dialog (E10-F02-T02) — the headerless three-button card.
 *
 * Parity with the legacy `<dialog id="unsaved-dialog">`: a plain white card (no
 * navy header) warning the user about unsaved project changes, with three
 * actions:
 *   - "Save and exit"     — gold primary (bg-accent #d4af37 / text-primary
 *     slate-900); persists then performs the deferred navigation;
 *   - "Discard and exit"  — outline; drops the draft then navigates;
 *   - "Cancel (Stay)"     — ghost; closes the dialog and stays put.
 *
 * It composes the shared {@link Dialog} primitive (headerless variant: `title`
 * omitted → plain white card). Esc and a scrim click both map to Cancel/Stay —
 * the conservative choice that never silently discards work. The footer order
 * (Cancel · Discard · Save, left→right) puts the safe action first and the
 * confirming gold action last, matching the live app and the {@link Dialog}
 * footer convention (right-aligned, primary action rightmost).
 *
 * The dialog is purely presentational: the provider ({@link UnsavedGuardProvider})
 * owns open/save state and the deferred navigation; this component only renders
 * the card and forwards button clicks.
 */
import { Dialog } from './Dialog.js';

export interface UnsavedDialogProps {
  /** Whether the dialog is shown (the provider opens it on a dirty navigation). */
  readonly open: boolean;
  /** Save-in-flight: disables all actions and relabels the primary button. */
  readonly saving?: boolean;
  /** Save and exit — persist then proceed with the deferred navigation. */
  readonly onSave: () => void;
  /** Discard and exit — drop the draft then proceed. */
  readonly onDiscard: () => void;
  /** Cancel (Stay) — close and keep editing. Also fired by Esc / scrim click. */
  readonly onCancel: () => void;
}

export function UnsavedDialog({
  open,
  saving = false,
  onSave,
  onDiscard,
  onCancel,
}: UnsavedDialogProps): React.JSX.Element | null {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      ariaLabel="Unsaved changes"
      testId="unsaved-dialog"
      // Esc / scrim close maps to Cancel; while saving suppress dismissal so a
      // half-finished navigation can't be triggered mid-save.
      disableEscapeClose={saving}
      disableBackdropClose={saving}
      footer={
        <>
          <button
            type="button"
            data-testid="unsaved-dialog-cancel"
            onClick={onCancel}
            disabled={saving}
            className="rounded px-4 py-2 text-sm font-medium text-text-muted hover:bg-gray-50 hover:text-text-strong disabled:opacity-60"
          >
            Cancel (Stay)
          </button>
          <button
            type="button"
            data-testid="unsaved-dialog-discard"
            onClick={onDiscard}
            disabled={saving}
            className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 disabled:opacity-60"
          >
            Discard and exit
          </button>
          <button
            type="button"
            data-testid="unsaved-dialog-save"
            onClick={onSave}
            disabled={saving}
            className="rounded bg-accent px-4 py-2 text-sm font-semibold text-primary hover:bg-accent-hover disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save and exit'}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <i className="fa-solid fa-triangle-exclamation mt-0.5 text-warning" aria-hidden />
        <div>
          <p className="brand-font text-base font-semibold text-text-strong">
            You have unsaved changes
          </p>
          <p className="mt-1 text-sm text-text-muted">
            Save them before leaving, discard them, or stay on this page to keep editing.
          </p>
        </div>
      </div>
    </Dialog>
  );
}
